<template>
  <full-bleed-layout :show-leaderboard-ad="loading === 0">
    <loading-spinner v-if="loading !== 0" />
    <div v-else-if="user" class="member-profile">
      <profile-header :user="user" class="profile-header--show-mobile" />
      <div class="member-profile__overview-container"> <!-- Here to better match the DOM structure of ProfileComponent -->
        <div class="member-profile__summaries">
          <div class="member-profile__actions member-profile__actions--mobile">
            <start-conversation-button
              v-if="showsPrivateMessaging"
              class="member-profile__button button--block"
              :user="user"
            >
              <icon-component
                name="envelope"
                title="envelope icon"
                class="member-profile__button-icon button__icon"
              />
              Message
            </start-conversation-button>
            <follow-button
              :currently-following="followedByCurrentUser"
              is-in-profile-tooltip-or-member-profile
              :user="user"
              class="member-profile__button button--block"
            />
          </div>
          <user-status
            v-if="siteHasFeature('status_updates') && user.profileStatus"
            class="member-profile__user-status member-profile__user-status--mobile"
            :user="user"
          />
          <div class="member-profile__summary-group member-profile__summary-group--right">
            <div class="member-profile__actions member-profile__actions--desktop">
              <start-conversation-button
                v-if="showsPrivateMessaging"
                class="member-profile__button button--block"
                :user="user"
              >
                <icon-component
                  name="envelope"
                  title="envelope icon"
                  class="member-profile__button-icon button__icon"
                />
                Message
              </start-conversation-button>
              <follow-button
                :currently-following="followedByCurrentUser"
                is-in-profile-tooltip-or-member-profile
                :user="user"
                class="member-profile__button button--block"
              />
            </div>
            <activity-stream-summary :link="activityLink" :user="user" />
          </div>
          <div class="member-profile__summary-group">
            <user-status
              v-if="siteHasFeature('status_updates') && user.profileStatus"
              class="member-profile__user-status member-profile__user-status--desktop"
              :user="user"
            />
            <user-bio-summary
              v-if="siteIsSHN && user.bio"
              :user="user"
            />
            <about-me-summary v-if="!siteIsSHN" :user="user" />
            <social-sites-list
              v-if="siteIsSHN"
              :user="user"
            />
            <health-profile-summary v-if="siteIsSHN" :user="user" />
            <awards-summary
              v-if="siteIsSHN"
              :user="user"
              :link-href="`/members/${user.username}/awards`"
            />
          </div>
        </div>
      </div>
    </div>
  </full-bleed-layout>
</template>

<script>
import AboutMeSummary from '@/components/User/Summary/AboutMeSummary.vue';
import ActivityStreamSummary from '@/components/User/Summary/ActivityStreamSummary.vue';
import AwardsSummary from '@/components/User/Summary/AwardsSummary.vue';
import ProfileHeader from '@/components/User/ProfileHeader.vue';
import UserStatus from '@/components/User/Statuses/UserStatus.vue';
import FollowButton from '@/components/User/Following/FollowButton.vue';
import StartConversationButton from '@/components/PrivateMessaging/StartConversationButton.vue';
import HealthProfileSummary from '@/components/User/Summary/HealthProfileSummary.vue';
import UserBioSummary from '@/components/User/Summary/UserBioSummary.vue';
import SocialSitesList from '@/components/User/Summary/SocialSitesList.vue';
import { GET_USER_PROFILE_QUERY } from '@/graphql/queries/user-queries';
import { isFeatureFlagEnabled } from '@/utils/utils';
import { mapGetters } from 'vuex';

export default {
  name: 'MemberProfile',
  components: {
    AboutMeSummary,
    ActivityStreamSummary,
    ProfileHeader,
    UserStatus,
    FollowButton,
    StartConversationButton,
    HealthProfileSummary,
    UserBioSummary,
    AwardsSummary,
    SocialSitesList,
  },
  data() {
    return {
      user: {},
      slug: this.$route.params.slug,
      loading: 0,
    };
  },
  metaInfo() {
    if (!this.loading && this.user) {
      return {
        title: `${this.user.username} Profile`,
        meta: [
          {
            name: 'robots',
            vmid: 'robots',
            content: 'noindex, follow',
          },
        ],
        link: [
          { vmid: 'canonical', content: null },
        ],
      };
    }
    return {};
  },
  computed: {
    ...mapGetters([
      'isAdmin',
      'siteHasFeature',
    ]),
    activityLink() {
      return `/members/${this.user.slug}/activity`;
    },
    followedByCurrentUser() {
      return this.user.isFollowed;
    },
    profileBelongsToAdmin() {
      return [1, 5, 6].includes(this.user.roleId);
    },
    showsPrivateMessaging() {
      return !isFeatureFlagEnabled(this.$site, 'restrict_private_messaging') || this.isAdmin || this.profileBelongsToAdmin;
    },
    siteIsSHN() {
      return this.siteHasFeature('social_network');
    },
  },
  watch: {
    user(to) {
      if (!to) { return; }
      this.onLoad();
    },
  },
  methods: {
    onLoad() {
      const dataLayer = JSON.stringify({
        post_categories: ['profile'],
        has_user_generated_content: true,
      });
      this.$emit('loaded', dataLayer);
    },
  },
  apollo: {
    user: {
      query: GET_USER_PROFILE_QUERY,
      variables() {
        return {
          slug: this.slug.toLowerCase(),
        };
      },
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/stylesheets/components/_member-profile';
</style>
